import { ExternalConnectionDto } from '@/lib/integrationTypes/externalConnectionTypes';
import { useEffect, useMemo, useState } from 'react';
import ExternalFilter from './SingleFilterInput';
import { Button } from '@/components/ui/button';
import { FieldValues } from 'react-hook-form';
import { ExternalServiceOption } from '@/hooks/externalConnectionsApi';
import { Loader2 } from 'lucide-react';

type ExternalConnectionFilters = {
  externalConnection: ExternalConnectionDto | ExternalServiceOption;
  onChange: (data: FieldValues) => void;
  values: FieldValues;
  onSubmit?: (data: FieldValues) => void;
  processing: boolean;
  disabled?: boolean;
};

interface FilterResult {
  key: string;
  value: string | null;
  required: boolean;
}

const ExternalConnectionFiltersAlt = ({
  externalConnection,
  values,
  onChange,
  processing,
  onSubmit,
  disabled
}: ExternalConnectionFilters) => {
  const [filterState, setFilterState] = useState<FilterResult[]>(
    externalConnection.deviceCreationKeyValues.map((filter) => ({
      key: filter.key,
      value: values[filter.key] ?? null,
      required: filter.required,
    }))
  );

  const formValues = useMemo(
    () =>
      filterState
        .filter((x) => x.value)
        .reduce(
          (prev, { key, value }) => {
            prev[key] = value;
            return prev;
          },
          {} as { [x: string]: string | null }
        ),
    [filterState]
  );

  useEffect(() => {
    onChange(formValues);
  }, [formValues, onChange]);

  const totalFilters = externalConnection.deviceCreationKeyValues.length;

  let filters = externalConnection.deviceCreationKeyValues.map((filter, i) => {
    const { key, required } = filter;
    const isLast = i === totalFilters - 1;

    let filterKeysNotSelectedYet: string[] = [];

    function optionFilter(option: Record<string, string>): boolean {
      return filterState.slice(0, i).every((filter) => {
        if (filter.value === null) {
          if (!filterKeysNotSelectedYet.includes(filter.key)) {
            filterKeysNotSelectedYet.push(filter.key);
          }
          return true;
        } else {
          return option[filter.key] === filter.value;
        }
      });
    }

    const options = externalConnection.deviceCreationChoices.filter(optionFilter).reduce(
      (acc, item) => {
        if (
          !acc.some((e) =>
            [key, ...filterKeysNotSelectedYet].every(
              (filterKey) => e[filterKey] === item[filterKey]
            )
          )
        ) {
          acc.push(item);
        }
        return acc;
      },
      [] as Record<string, string>[]
    );

    const connectButton = isLast && onSubmit ? (
      <Button
        disabled={
          processing || filterState.every((filter) => !filter.required || filter.value == null)
        }
        onClick={() => onSubmit(formValues)}
        className="gap-2"
      >
        {processing && <Loader2 className="h-4 w-4 animate-spin" />}
        Connect
      </Button>
    ) : undefined;

    return (
      <ExternalFilter
        key={i}
        filter={filter}
        options={options}
        disabled={disabled}
        selected={filterState[i].value}
        setSelected={(option) => {
          if (option) {
            setFilterState((prev) =>
              prev.map((filterthing) => ({
                ...filterthing,
                value: [key, ...filterKeysNotSelectedYet].includes(filterthing.key)
                  ? option[filterthing.key]
                  : filterthing.value,
              }))
            );
          } else {
            setFilterState((prev) =>
              prev.map((filterthing) => ({
                ...filterthing,
                value: filterthing.key === key ? null : filterthing.value,
              }))
            );
          }
        }}
        extraKeysToInclude={filterKeysNotSelectedYet}
        required={required}
        connectButton={connectButton}
      />
    );
  });

  return (
    <div className="flex flex-col gap-4 pt-4">
      {filters}
    </div>
  );
};

export default ExternalConnectionFiltersAlt;
